import React from 'react';
import {
  Bold,
  Italic,
  Underline,
  Link as LinkIcon,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Paperclip,
  Forward,
  Reply,
  ReplyAll,
  CornerDownLeft,
  Sparkles,
  X as XIcon,
  GripHorizontal,
} from 'lucide-react';
import { ReplyToolbarButton } from './ComposeEmailModal';
import { isHtmlEditorEmpty, isHtmlEditorVisuallyEmpty } from '../../../utils/mailBody';
import { handleContentEditableListKeyDown } from '../../../utils/contentEditableLists';

type ReplyMode = 'reply' | 'replyAll';

const EDITOR_HEIGHT_STORAGE_KEY = 'meisterpfad_inbox_reply_editor_height';
const EDITOR_HEIGHT_DEFAULT = 180;
const EDITOR_HEIGHT_MIN = 120;
const EDITOR_HEIGHT_MAX_VH = 0.7;

function clampEditorHeight(px: number): number {
  const max = Math.max(EDITOR_HEIGHT_MIN, Math.round(window.innerHeight * EDITOR_HEIGHT_MAX_VH));
  return Math.min(max, Math.max(EDITOR_HEIGHT_MIN, Math.round(px)));
}

function readStoredEditorHeight(): number {
  try {
    const raw = localStorage.getItem(EDITOR_HEIGHT_STORAGE_KEY);
    const n = raw ? Number(raw) : NaN;
    if (Number.isFinite(n)) return clampEditorHeight(n);
  } catch {
    /* ignore */
  }
  return EDITOR_HEIGHT_DEFAULT;
}

interface InboxComposeAreaProps {
  editorRef: React.RefObject<HTMLDivElement | null>;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  /** HTML from the contentEditable editor. */
  replyDraft: string;
  onReplyInput: (html: string) => void;
  onSend: (modeOverride?: ReplyMode) => void;
  canReplyAll: boolean;
  replyMode: ReplyMode;
  onReplyMode: (mode: ReplyMode) => void;
  sending?: boolean;
  contactName: string;
  replyCc: string;
  onReplyCc: (v: string) => void;
  showReplyCc: boolean;
  onToggleCc: () => void;
  replyAttachments: File[];
  onFilesSelected: (files: File[]) => void;
  onOpenAttachments: () => void;
  onForward: () => void;
  onLinkButton: () => void;
  // AI
  mailId: string | number;
  suggestions: string[];
  aiPrompt: string;
  onAiPrompt: (v: string) => void;
  aiLoading: boolean;
  aiError: string | null;
  onGenerateAiDraft: (prompt?: string) => void;
}

export function InboxComposeArea({
  editorRef,
  fileInputRef,
  replyDraft,
  onReplyInput,
  onSend,
  canReplyAll,
  replyMode,
  onReplyMode,
  sending = false,
  contactName,
  replyCc,
  onReplyCc,
  showReplyCc,
  onToggleCc,
  replyAttachments,
  onFilesSelected,
  onOpenAttachments,
  onForward,
  onLinkButton,
  mailId,
  suggestions,
  aiPrompt,
  onAiPrompt,
  aiLoading,
  aiError,
  onGenerateAiDraft,
}: InboxComposeAreaProps) {
  const [aiOpen, setAiOpen] = React.useState(false);
  const aiInputRef = React.useRef<HTMLInputElement | null>(null);
  const [editorHeight, setEditorHeight] = React.useState(readStoredEditorHeight);
  const resizeDragRef = React.useRef<{ startY: number; startHeight: number } | null>(null);

  React.useEffect(() => {
    setAiOpen(false);
  }, [mailId]);

  // Collapse the AI panel once a draft lands in the editor.
  React.useEffect(() => {
    if (!aiLoading && aiOpen && replyDraft) {
      setAiOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [replyDraft]);

  React.useEffect(() => {
    const onResize = () => setEditorHeight((h) => clampEditorHeight(h));
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  React.useEffect(() => {
    try {
      localStorage.setItem(EDITOR_HEIGHT_STORAGE_KEY, String(editorHeight));
    } catch {
      /* ignore */
    }
  }, [editorHeight]);

  const clearResizeBodyStyles = React.useCallback(() => {
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
    document.body.style.touchAction = '';
    document.body.style.overscrollBehavior = '';
    document.documentElement.style.overscrollBehavior = '';
  }, []);

  React.useEffect(() => {
    return () => {
      resizeDragRef.current = null;
      clearResizeBodyStyles();
    };
  }, [clearResizeBodyStyles]);

  const onResizePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    resizeDragRef.current = { startY: e.clientY, startHeight: editorHeight };
    e.currentTarget.setPointerCapture(e.pointerId);
    document.body.style.cursor = 'ns-resize';
    document.body.style.userSelect = 'none';
    // Block browser pull-to-refresh / overscroll while resizing on mobile.
    document.body.style.touchAction = 'none';
    document.body.style.overscrollBehavior = 'none';
    document.documentElement.style.overscrollBehavior = 'none';
  };

  const onResizePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = resizeDragRef.current;
    if (!drag) return;
    e.preventDefault();
    // Dragging the top handle upward expands the editor.
    const next = clampEditorHeight(drag.startHeight + (drag.startY - e.clientY));
    setEditorHeight(next);
  };

  const onResizePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!resizeDragRef.current) return;
    resizeDragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
    clearResizeBodyStyles();
  };

  const syncFromEditor = () => {
    if (editorRef.current) onReplyInput(editorRef.current.innerHTML || '');
  };

  const editorHtml = editorRef.current?.innerHTML || replyDraft;
  const isEditorEmpty = () => isHtmlEditorEmpty(editorHtml);
  const showPlaceholder = isHtmlEditorVisuallyEmpty(editorHtml);

  const exec = (command: string) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand(command);
      syncFromEditor();
    }
  };

  return (
    <div className="shrink-0 border-t border-app-border bg-app-surface min-w-0 overflow-hidden">
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Antwortfeld vergrößern oder verkleinern"
        aria-valuemin={EDITOR_HEIGHT_MIN}
        aria-valuenow={editorHeight}
        tabIndex={0}
        onPointerDown={onResizePointerDown}
        onPointerMove={onResizePointerMove}
        onPointerUp={onResizePointerUp}
        onPointerCancel={onResizePointerUp}
        onLostPointerCapture={onResizePointerUp}
        onKeyDown={(e) => {
          if (e.key === 'ArrowUp') {
            e.preventDefault();
            setEditorHeight((h) => clampEditorHeight(h + 24));
          } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setEditorHeight((h) => clampEditorHeight(h - 24));
          }
        }}
        className="group flex h-5 md:h-3 touch-none cursor-ns-resize items-center justify-center hover:bg-app-hover active:bg-app-surface-muted select-none"
        title="Ziehen zum Vergrößern"
        style={{ touchAction: 'none' }}
      >
        <GripHorizontal className="h-4 w-4 text-app-text-subtle group-hover:text-app-text-muted md:h-3.5 md:w-3.5" aria-hidden />
      </div>

      {/* KI-Antwort entwerfen panel */}
      {aiOpen && (
        <div className="mx-3 mt-3 rounded-xl border border-violet-200 bg-violet-50 p-3">
          <div className="flex items-center gap-2 mb-2">
            <span className="flex items-center justify-center w-6 h-6 rounded-md bg-gradient-to-br from-violet-600 to-blue-500">
              <Sparkles className="w-3.5 h-3.5 text-white" />
            </span>
            <span className="text-xs font-semibold text-violet-700">KI-Antwort entwerfen</span>
            <button
              type="button"
              onClick={() => setAiOpen(false)}
              className="ml-auto p-1 rounded text-violet-500 hover:bg-violet-100"
              aria-label="Schließen"
            >
              <XIcon className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-violet-200 bg-app-surface px-2.5 py-1.5">
            <input
              ref={aiInputRef}
              value={aiPrompt}
              onChange={(e) => onAiPrompt(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !aiLoading) onGenerateAiDraft(aiPrompt);
              }}
              placeholder="Beschreibe, was die KI antworten soll …"
              className="flex-1 text-xs text-app-text outline-none bg-transparent"
            />
            <button
              type="button"
              onClick={() => onGenerateAiDraft(aiPrompt)}
              disabled={aiLoading}
              className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-br from-violet-600 to-blue-500 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-70"
            >
              <Sparkles className="w-3 h-3" />
              {aiLoading ? 'Entwirft…' : 'Entwerfen'}
            </button>
          </div>
          {suggestions.length > 0 && (
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-medium text-violet-600">Vorlagen:</span>
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  type="button"
                  disabled={aiLoading}
                  onClick={() => {
                    onAiPrompt(s);
                    onGenerateAiDraft(s);
                  }}
                  className="rounded-full border border-violet-200 bg-app-surface px-2.5 py-1 text-xs font-medium text-violet-700 hover:bg-violet-100 disabled:opacity-60"
                >
                  {s}
                </button>
              ))}
            </div>
          )}
          {aiError && (
            <div className="mt-2 rounded-md border border-red-100 bg-red-50 px-2 py-1.5 text-xs text-red-600">
              {aiError}
            </div>
          )}
        </div>
      )}

      {/* editor */}
      <div className="relative overflow-y-auto" style={{ height: editorHeight }}>
        {showPlaceholder && (
          <span className="pointer-events-none absolute left-3 top-2.5 text-sm text-app-text-subtle">
            {replyMode === 'replyAll' ? 'Antwort an alle verfassen…' : `Antwort an ${contactName} verfassen…`}
          </span>
        )}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={(e) => onReplyInput(e.currentTarget.innerHTML || '')}
          onKeyDown={(e) => {
            if (
              (e.ctrlKey || e.metaKey) &&
              e.key === 'Enter' &&
              !e.shiftKey &&
              !sending &&
              !isEditorEmpty()
            ) {
              e.preventDefault();
              onSend(canReplyAll && replyMode === 'replyAll' ? 'replyAll' : 'reply');
              return;
            }
            if (handleContentEditableListKeyDown(e, editorRef.current)) {
              syncFromEditor();
            }
          }}
          className="w-full min-h-full px-3 py-2.5 text-sm outline-none whitespace-pre-wrap [&_p]:mb-3 [&_p:last-child]:mb-0 [&_ul]:list-disc [&_ol]:list-decimal [&_ul]:pl-5 [&_ol]:pl-5 [&_li]:list-outside"
        />
      </div>
      {/* Cc row */}
      {showReplyCc && (
        <div className="border-t border-app-border px-3 py-2">
          <input
            type="text"
            value={replyCc}
            onChange={(e) => onReplyCc(e.target.value)}
            placeholder="Cc: cc@beispiel.de, weitere@…"
            className="w-full border border-app-border rounded-lg px-3 py-1.5 text-xs focus:ring-2 focus:ring-pipedrive-blue/20 outline-none"
          />
        </div>
      )}
      {/* toolbar */}
      <div>
        <div className="flex items-center gap-0.5 px-3 py-2 overflow-x-auto [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden md:overflow-visible">
          <ReplyToolbarButton icon={Bold} title="Fett" onClick={() => exec('bold')} />
          <ReplyToolbarButton icon={Italic} title="Kursiv" onClick={() => exec('italic')} />
          <ReplyToolbarButton icon={Underline} title="Unterstrichen" onClick={() => exec('underline')} />
          <span className="w-px h-4 bg-app-border mx-1 shrink-0" />
          <ReplyToolbarButton icon={LinkIcon} title="Link einfügen" onClick={onLinkButton} />
          <ReplyToolbarButton icon={List} title="Aufzählung" onClick={() => exec('insertUnorderedList')} />
          <ReplyToolbarButton icon={ListOrdered} title="Nummerierte Liste" onClick={() => exec('insertOrderedList')} />
          <span className="hidden md:contents">
            <span className="w-px h-4 bg-app-border mx-1" />
            <ReplyToolbarButton icon={AlignLeft} title="Linksbündig" onClick={() => exec('justifyLeft')} />
            <ReplyToolbarButton icon={AlignCenter} title="Zentriert" onClick={() => exec('justifyCenter')} />
            <ReplyToolbarButton icon={AlignRight} title="Rechtsbündig" onClick={() => exec('justifyRight')} />
          </span>
          <span className="w-px h-4 bg-app-border mx-1 shrink-0" />
          <ReplyToolbarButton icon={Paperclip} title="Datei anhängen" onClick={() => fileInputRef.current?.click()} />
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="hidden"
            onChange={(e) => {
              const files = Array.from(e.target.files || []);
              onFilesSelected(files);
              e.target.value = '';
            }}
          />
          {replyAttachments.length > 0 && (
            <button
              type="button"
              onClick={onOpenAttachments}
              className="ml-1 shrink-0 text-xs font-medium text-app-text-subtle hover:text-pipedrive-blue hover:underline"
            >
              {replyAttachments.length === 1 ? '1' : String(replyAttachments.length)}
              <span className="hidden md:inline">
                {replyAttachments.length === 1 ? ' Datei' : ' Dateien'}
              </span>
            </button>
          )}
          <ReplyToolbarButton icon={Forward} title="E-Mail weiterleiten" onClick={onForward} />
          <button
            type="button"
            onClick={onToggleCc}
            className={`inline-flex shrink-0 items-center justify-center h-7 rounded-md px-2 text-xs font-medium ${
              showReplyCc ? 'bg-app-surface-muted text-app-text' : 'text-app-text-subtle hover:bg-app-hover hover:text-app-text'
            }`}
            title={showReplyCc ? 'Cc ausblenden' : 'Cc einblenden'}
          >
            Cc
          </button>

          {/* Desktop actions stay on the same row */}
          <div className="hidden md:contents">
            <div className="flex-1 min-w-2" />
            <span className="w-px h-4 bg-app-border mx-1" />
            <button
              type="button"
              onClick={() => {
                setAiOpen((o) => !o);
                setTimeout(() => aiInputRef.current?.focus(), 30);
              }}
              className={`inline-flex h-9 items-center gap-1.5 rounded-md px-3.5 text-sm font-semibold ${
                aiOpen
                  ? 'border border-violet-200 bg-app-surface text-violet-700'
                  : 'bg-gradient-to-br from-violet-600 to-blue-500 text-white'
              }`}
              title="KI-Antwortentwurf"
            >
              <Sparkles className="w-3.5 h-3.5" />
              KI-Antwort
            </button>
            <button
              type="button"
              onClick={() => {
                onReplyMode('reply');
                onSend('reply');
              }}
              disabled={sending || isEditorEmpty()}
              title="Antworten"
              className={`inline-flex h-9 items-center gap-2 rounded-md px-3.5 text-sm font-semibold transition-colors disabled:opacity-40 ${
                canReplyAll
                  ? 'border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100'
                  : 'bg-pipedrive-green text-white hover:bg-[#218838]'
              }`}
            >
              <Reply className="w-3.5 h-3.5" />
              {sending && replyMode === 'reply' ? 'Wird gesendet…' : 'Antworten'}
            </button>
            {canReplyAll && (
              <button
                type="button"
                onClick={() => {
                  onReplyMode('replyAll');
                  onSend('replyAll');
                }}
                disabled={sending || isEditorEmpty()}
                title="Allen antworten"
                className="inline-flex h-9 items-center gap-2 rounded-md bg-pipedrive-green pl-3.5 pr-2.5 text-sm font-semibold text-white hover:bg-[#218838] disabled:opacity-40"
              >
                <ReplyAll className="w-3.5 h-3.5" />
                {sending && replyMode === 'replyAll' ? 'Wird gesendet…' : 'Allen antworten'}
                {!sending && (
                  <span
                    aria-hidden
                    className="inline-flex items-center gap-0.5 rounded border border-white/30 bg-white/15 px-1.5 py-0.5 text-xs font-medium leading-none text-white/95"
                  >
                    Strg
                    <CornerDownLeft className="w-3 h-3" strokeWidth={2.5} />
                  </span>
                )}
              </button>
            )}
          </div>
        </div>

        {/* Mobile action row — primary CTAs get full width */}
        <div className="flex md:hidden items-center gap-2 px-2 pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-0.5">
          <button
            type="button"
            onClick={() => {
              setAiOpen((o) => !o);
              setTimeout(() => aiInputRef.current?.focus(), 30);
            }}
            className={`inline-flex h-9 shrink-0 items-center justify-center gap-1.5 rounded-md px-3 text-xs font-semibold ${
              aiOpen
                ? 'border border-violet-200 bg-app-surface text-violet-700'
                : 'bg-gradient-to-br from-violet-600 to-blue-500 text-white'
            }`}
            title="KI-Antwortentwurf"
          >
            <Sparkles className="w-3.5 h-3.5" />
            KI
          </button>
          <button
            type="button"
            onClick={() => {
              onReplyMode('reply');
              onSend('reply');
            }}
            disabled={sending || isEditorEmpty()}
            title="Antworten"
            className={`inline-flex h-9 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-md px-3 text-xs font-semibold transition-colors disabled:bg-app-surface-muted disabled:text-app-text-subtle ${
              canReplyAll
                ? 'border border-emerald-200 bg-emerald-50 text-emerald-700'
                : 'bg-pipedrive-green text-white'
            }`}
          >
            <Reply className="w-3.5 h-3.5 shrink-0" />
            <span className="truncate">{sending && replyMode === 'reply' ? 'Senden…' : 'Antworten'}</span>
          </button>
          {canReplyAll && (
            <button
              type="button"
              onClick={() => {
                onReplyMode('replyAll');
                onSend('replyAll');
              }}
              disabled={sending || isEditorEmpty()}
              title="Allen antworten"
              className="inline-flex h-9 min-w-0 flex-1 items-center justify-center gap-1.5 rounded-md bg-pipedrive-green px-3 text-xs font-semibold text-white disabled:bg-app-surface-muted disabled:text-app-text-subtle"
            >
              <ReplyAll className="w-3.5 h-3.5 shrink-0" />
              <span className="truncate">{sending && replyMode === 'replyAll' ? 'Senden…' : 'Allen'}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
